import React, { SVGAttributes } from 'react';
import { useFela } from '../../..';
import { LoadingAnimationProps } from '../LoadingAnimationProps';

const keyframes = () => ({
  from: {
    transform: 'rotate(0deg)',
  },
  to: {
    transform: 'rotate(360deg)',
  },
});

const spin = (renderer: any) => {
  const animation = renderer.renderKeyframe(keyframes, {});

  return {
    animationName: animation,
    animationDuration: '5000ms',
    animationIterationCount: 'infinite',
    animationTimingFunction: 'linear',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    margin: 'auto',
  };
};

const SIZE = 1000;
const CENTER = SIZE / 2;
const BORDER_OFFSET = 30;
const STROKE_WIDTH = 40;
const wingOffsetX = 90;
const wingOffsetY = wingOffsetX * 0.9;

const Wings = ({ rotation }: { rotation: number }) => {
  const { theme } = useFela();
  const d1 = 120;
  const d2 = 220;

  const strokeStyle: SVGAttributes<SVGLineElement> = {
    strokeWidth: STROKE_WIDTH,
    stroke: theme.palette.color2.hex,
    strokeLinecap: 'round',
  };
  const arm: SVGAttributes<SVGLineElement> = {
    x1: CENTER,
    x2: CENTER,
    y1: CENTER,
    y2: BORDER_OFFSET,
  };
  const wingTop: SVGAttributes<SVGLineElement> = {
    y1: BORDER_OFFSET + d1,
    y2: BORDER_OFFSET + d1 - wingOffsetY,
  };
  const wingCenter: SVGAttributes<SVGLineElement> = {
    y1: BORDER_OFFSET + d2,
    y2: BORDER_OFFSET + d2 - wingOffsetY,
  };
  const wingBottom: SVGAttributes<SVGLineElement> = {
    y1: BORDER_OFFSET + 327,
    y2: BORDER_OFFSET + 327 - wingOffsetY,
  };
  const wingLeft: SVGAttributes<SVGLineElement> = {
    x1: CENTER,
    x2: CENTER + wingOffsetX,
  };
  const wingRight: SVGAttributes<SVGLineElement> = {
    x1: CENTER,
    x2: CENTER - wingOffsetX,
  };
  return (
    <g transform={`rotate(${rotation}, ${CENTER}, ${CENTER})`}>
      <line {...arm} {...strokeStyle} />
      <line {...wingLeft} {...wingTop} {...strokeStyle} />
      <line {...wingRight} {...wingTop} {...strokeStyle} />
      <line {...wingLeft} {...wingCenter} {...strokeStyle} />
      <line {...wingRight} {...wingCenter} {...strokeStyle} />
      <line {...wingLeft} {...wingBottom} {...strokeStyle} />
      <line {...wingRight} {...wingBottom} {...strokeStyle} />
    </g>
  );
};

const Snowflake = ({
  size,
  dmId = 'dm-snowflake',
  id,
  title,
  description,
}: LoadingAnimationProps): JSX.Element => {
  const { css, renderer } = useFela();

  const arms = [];
  const armCount = 8;
  for (let index = 0; index < armCount; index += 1) {
    const rotation = index * (360 / armCount);
    arms.push(<Wings rotation={rotation} key={rotation} />);
  }

  // Accessibility attributes
  const TITLE_ID = `${id || dmId}Title`; // todo: dmId should be removed, as soon as id prop is required (myt-7757)
  const DESC_ID = description ? ` ${id || dmId}Desc` : ''; // todo: dmId should be removed, as soon as id prop is required (myt-7757)
  const ARIA_LABELLEDBY = `${TITLE_ID}${DESC_ID}`;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 1000 1000"
      width={size * 1.5}
      height={size * 1.5}
      preserveAspectRatio="xMidYMid meet"
      aria-labelledby={ARIA_LABELLEDBY}
      role="img"
      className={css(spin(renderer))}>
      <title id={TITLE_ID}>{title}</title>
      {description && <desc id={DESC_ID}>{description}</desc>}
      {arms}
    </svg>
  );
};

export default Snowflake;
